import { create } from "zustand";

import { DEFAULT_CLOSED_WORKSPACE_TAB_IDS } from "@/components/workspaces/workspace-tabs.config";
import type { NamespaceId } from "@/types/namespaces";

export type WorkspaceTabsState = {
  closedTabs: NamespaceId[];
  closeTab: (id: NamespaceId) => void;
  reopenTab: (id: NamespaceId) => void;
  resetTabs: () => void;
  setClosedTabs: (ids: NamespaceId[]) => void;
};

export const useWorkspaceTabsStore = create<WorkspaceTabsState>((set) => ({
  // Seeded with the default-closed set so the rail hides those tabs before the
  // preference bridge bootstraps (and for unauthenticated sessions, where the
  // bridge stays disabled). An explicit saved preference overrides this on
  // bootstrap via setClosedTabs.
  closedTabs: [...DEFAULT_CLOSED_WORKSPACE_TAB_IDS],
  closeTab: (id) =>
    set((state) =>
      state.closedTabs.includes(id)
        ? state
        : { ...state, closedTabs: [...state.closedTabs, id] },
    ),
  reopenTab: (id) =>
    set((state) => ({
      ...state,
      closedTabs: state.closedTabs.filter((tabId) => tabId !== id),
    })),
  resetTabs: () => set({ closedTabs: [...DEFAULT_CLOSED_WORKSPACE_TAB_IDS] }),
  setClosedTabs: (ids) =>
    set((state) => {
      const uniqueTabs = ids.filter((tabId, index) => tabId != null && ids.indexOf(tabId) === index);
      return uniqueTabs.every((tabId, index) => state.closedTabs[index] === tabId) &&
        state.closedTabs.length === uniqueTabs.length
        ? state
        : { ...state, closedTabs: uniqueTabs };
    }),
}));
